import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { Button, ButtonColor, ButtonSize } from '../../store/models/button.model';
import { CampaignService } from '../../store/services/campaign.service';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IBlockedDate, IBlockedDateRule, IBlockedDateRules } from '../../store/models/campaigns.model';
import { DataSourceParams, ETableColumnType, ITableColumn } from '../../store/models/table.model';
import { BaseDataSource } from '../../datasources/base.datasource';
import { StaticDataSource } from '../../datasources/static.datasource';
import { IAccount } from '../../store/models/account.model';
import { AccountService } from '../../store/services/account.service';
import { UserType } from '../../store/models/user.model';
import { openDialog, openSnackbar } from '../../store/actions/page.action';
import { AddBlockedDateRuleComponent } from '../add-blocked-date-rule/add-blocked-date-rule.component';

@Component({
  selector: 'app-blocked-date-rules',
  templateUrl: './blocked-date-rules.component.html',
  styleUrls: ['./blocked-date-rules.component.scss'],
})
export class BlockedDateRulesComponent implements OnInit, OnDestroy {

  @Input() canAdd: boolean;
  @Input() canDelete = false;
  @Input() createRule = true;
  @Input() rules: IBlockedDateRules[];

  @Input() dateId: number;

  @Output() changed: EventEmitter<IBlockedDateRules> = new EventEmitter<IBlockedDateRules>();
  @Output() deleted: EventEmitter<IBlockedDateRules> = new EventEmitter<IBlockedDateRules>();

  managers: IAccount[] = [];
  partners: IAccount[] = [];

  componentReady = false;

  addButtonProperties: Button = {
    text: 'Add',
    color: ButtonColor.GREEN,
    size: ButtonSize.NORMAL,
    onClick: () => this.add(),
  };

  blockDate: IBlockedDate;

  tableColumns: ITableColumn<IBlockedDateRules>[] = [];
  dataSource: BaseDataSource<IBlockedDateRules>;
  dataSourceParams: DataSourceParams = {
    page: 1,
    pageSize: 10,
    sortDir: 'desc',
    sortAttr: 'id',
    filters: {},
  };

  private canBeDeleted = false;

  private readonly destroy$: Subject<void> = new Subject<void>();

  constructor(
    private readonly store$: Store<IReducers>,
    private readonly campaignService: CampaignService,
    private readonly accountService: AccountService,
  ) {
  }

  ngOnInit(): void {
    forkJoin([
      this.accountService.getAccountsBasicInfo(UserType.PARTNER),
      this.accountService.getAccountsBasicInfo(UserType.MANAGER),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.partners = data[0].items;
        this.managers = data[1].items;

        this.getDateData();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  private prepareTableColumns(): void {
    this.tableColumns = [
      {
        id: 'blocked_emails',
        title: 'Blocked Emails',
        renderer: row => row.rule.blockType.join(','),
      },
      {
        id: 'managers',
        title: 'Managers',
        renderer: row => row.rule.managers.map(m => {
          const targetAccount = this.managers.find(mm => mm.account_id === m);

          if (targetAccount) {
            return `${targetAccount.first_name} ${targetAccount.last_name} (${targetAccount.email})`;
          }

          return '';
        }).join(','),
      },
      {
        id: 'partners',
        title: 'Partners',
        renderer: row => row.rule.partners.map(m => {
          const targetAccount = this.partners.find(mm => mm.account_id === m);

          if (targetAccount) {
            return `${targetAccount.first_name} ${targetAccount.last_name} (${targetAccount.email})`;
          }

          return '';
        }).join(','),
      },
      {
        id: 'states',
        title: 'States',
        renderer: row => row.rule.states.join(','),
      },
      {
        id: 'timezones',
        title: 'Timezones',
        renderer: row => row.rule.timezones.join(','),
      },
      {
        id: 'delete',
        title: 'Delete',
        type: ETableColumnType.BUTTON,
        disabled: !this.canDelete || !this.canBeDeleted,
        buttonProperties: {
          text: 'Delete',
          color: ButtonColor.RED,
          size: ButtonSize.NORMAL,
          onClick: element => this.deleteRule(element),
        },
      },
    ];
  }

  private add() {
    this.store$.dispatch(openDialog({
      dialogData: {
        content: AddBlockedDateRuleComponent,
        componentData: {
          partners: this.partners,
          managers: this.managers,
        },
        title: 'Add new rule',
        canClose: true,
        buttons: [
          {
            text: 'Add',
            color: ButtonColor.GREEN,
            size: ButtonSize.NORMAL,
            onClick: (dialog) => {
              const data = dialog.dialogForm.value;
              if (data.managers) {
                data.managers = data.managers.map(m => m.value);
              } else {
                data.managers = [];
              }
              if (data.partners) {
                data.partners = data.partners.map(m => m.value);
              } else {
                data.partners = [];
              }
              if (data.timezones) {
                data.timezones = data.timezones.map(m => m.value);
              } else {
                data.timezones = [];
              }
              if (data.states) {
                data.states = data.states.map(m => m.value);
              } else {
                data.states = [];
              }
              if (data.block_type) {
                data.block_type = data.block_type.map(m => m.value);
              } else {
                data.block_type = [];
              }

              if (this.createRule) {
                this.campaignService.addDateRule(this.dateId, data)
                  .subscribe(() => {
                    this.store$.dispatch(openSnackbar({
                      snackbarData: {
                        data: {
                          message: 'The rule was added successfully',
                          type: 'success',
                        },
                      },
                    }));
                    dialog.close();
                    this.getDateData();
                  }, err => {
                    this.store$.dispatch(openSnackbar({
                      snackbarData: {
                        data: {
                          message: err.error,
                          type: 'error',
                        },
                      },
                    }));
                    dialog.close();
                  });

                return;
              }

              dialog.close();
              this.changed.emit(data);
            },
          },
        ],
      },
    }));
  }

  private deleteRule(element: IBlockedDateRules) {
    this.store$.dispatch(openDialog({
      dialogData: {
        title: 'Deleting rule',
        content: 'Are you sure you want to delete?',
        canClose: true,
        closeButtonText: 'No',
        buttons: [
          {
            text: 'yes',
            color: ButtonColor.RED,
            size: ButtonSize.NORMAL,
            onClick: (dialog) => {
              if (!this.createRule) {
                this.deleted.emit(element);
                dialog.close();
                return;
              }
              this.campaignService.deleteBlockDateRule(this.dateId, element.id)
                .subscribe(() => {
                  this.store$.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: 'Rule was successfully deleted!',
                        type: 'success',
                      },
                    },
                  }));
                  dialog.close();
                  this.getDateData();
                }, err => {
                  this.store$.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: err.error,
                        type: 'error',
                      },
                    },
                  }));
                  dialog.close();
                });
            },
          },
        ],
      },
    }));
  }

  private getDateData() {
    console.log('here 2', this.rules);
    if (!this.createRule && this.rules) {
      this.canBeDeleted = this.rules.length > 1;
      this.dataSource = new StaticDataSource(this.rules);
      this.prepareTableColumns();
      this.componentReady = true;
      return;
    }
    this.campaignService.viewBlockedDate(this.dateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((date: IBlockedDate) => {
        this.blockDate = date;
        this.canBeDeleted = this.blockDate.blockedRules.length > 1;
        this.dataSource = new StaticDataSource(date.blockedRules);
        this.prepareTableColumns();
        this.componentReady = true;
      });
  }
}
